import { redactStreamUrl, type Tier } from '@app-iptv/shared'

import { clampSeekTarget, isKnownDuration } from './seek-target'
import type { MediaPlayer, MediaSource, PlayerErrorPayload, PlayerMediaInfo } from './types'

type EventHandler = () => void
type ErrorHandler = (err: PlayerErrorPayload) => void
type MediaInfoHandler = (info: PlayerMediaInfo) => void

/** `shaka.util.Error.Code.BAD_HTTP_STATUS`. Hard-coded pra não importar o
 *  módulo (lazy load) só por uma constante. */
const SHAKA_BAD_HTTP_STATUS = 1001

/** Teto (ms) pro fetch-probe de status do ramo native-ts. O `<video>` nativo só
 *  entrega `MediaError` (sem status HTTP); o probe (Range 0-0, só no erro)
 *  recupera 403/451 pra a máquina de failover/geo-block. Bounded pra que um
 *  probe travado (rede morta) NÃO segure o erro pra sempre — no pior caso emite
 *  só o `MediaError` e o consumer decide. */
const NATIVE_TS_PROBE_TIMEOUT_MS = 2500

/**
 * O `fetch` em uso é o POLYFILL (`whatwg-fetch`)? Ele só se instala sem `fetch`
 * nativo, ou seja no Chr38-41 (o piso), e marca a própria função com
 * `fetch.polyfill = true`: é a mesma detecção que o resto do app usa. Ali o `fetch` é
 * XHR por baixo, que só GUARDA o `mode` e faz a requisição com CORS.
 */
function isFetchPolyfilled(): boolean {
  return typeof fetch === 'function' && (fetch as unknown as { polyfill?: unknown }).polyfill === true
}

interface MpegtsPlayer {
  attachMediaElement(el: HTMLVideoElement): void
  load(): void
  /**
   * Aborta o loader (fetch), fecha+destrói o transmuxer e dá flush no
   * SourceBuffer. NÃO mexe em `video.src` — a MediaSource segue anexada,
   * então `load()` depois re-arma o pipeline sem re-attach.
   * (mpegts.js 1.8 `player-engine-main-thread.ts:unload()`.)
   */
  unload(): void
  play(): Promise<void>
  pause(): void
  destroy(): void
  /**
   * mpegts.js emite com N args posicionais (EventEmitter do pacote
   * `events`), NÃO um objeto único. Pro evento 'error' são 3:
   * `(errorType, errorDetail, errorInfo)`. Tipar como `(data: unknown)`
   * era um BUG silencioso: `data` chegava como a STRING do errorType e o
   * código lia `data[0]` (= primeira letra, 'N' de 'NetworkError') como
   * se fosse o tipo. Todo player_error do QoE subia como `mpegts:N`.
   */
  on(event: string, cb: (...args: unknown[]) => void): void
}

/** `errorInfo` do mpegts.js. Em NetworkError/HttpStatusCodeInvalid,
 *  `code` é o STATUS HTTP da origem (403/451/...). */
interface MpegtsErrorInfo {
  code?: number
  msg?: string
}

/** Payload do evento `MEDIA_INFO` do mpegts.js (`MSEPlayerMediaInfo`). Só
 *  lemos o subset que interessa pra decisão de audio-only — `hasVideo` é
 *  derivado do PMT (fonte de verdade). Campos opcionais: a lib pode emitir
 *  antes de todos estarem preenchidos. */
interface MpegtsMediaInfo {
  hasVideo?: boolean
  videoCodec?: string
  width?: number
  height?: number
}

/** Config de `mpegts.createPlayer` que passamos. Subset tipado do que a lib
 *  aceita — só o que de fato ajustamos. */
interface MpegtsConfig {
  type: string
  url: string
  isLive?: boolean
}

interface MpegtsModule {
  isSupported(): boolean
  createPlayer(config: MpegtsConfig, options?: MpegtsOptions): MpegtsPlayer
  /** Constantes de evento do mpegts.js (`mpegts.Events`). `MEDIA_INFO`
   *  dispara quando o transmuxer parseia o PMT + metadata do TS — traz
   *  `hasVideo`/codec/dimensões ANTES do 1º frame decodar. É a leitura
   *  confiável de "tem vídeo" (vs `video.videoWidth===0`, que é racy). */
  Events: { MEDIA_INFO: string }
}

/** Opções de runtime do mpegts.js (2º arg de `createPlayer`). Ver
 *  `mpegts.js/src/config.js` — só declaramos o que ajustamos. */
interface MpegtsOptions {
  enableStashBuffer?: boolean
  stashInitialSize?: number
  autoCleanupSourceBuffer?: boolean
  autoCleanupMaxBackwardDuration?: number
  autoCleanupMinBackwardDuration?: number
  lazyLoad?: boolean
  lazyLoadMaxDuration?: number
}

// ===== Preload do chunk do mpegts (perf/tv-live-cascade) =====
// Medido na LG do dono: o chunk do mpegts.js só COMEÇAVA a baixar depois do
// `/stream/sign` responder — porque o `await import()` mora dentro do runLoad,
// que roda depois do sign. Custo: +597ms EM SÉRIE no caminho do primeiro frame.
//
// O import é idempotente e o resultado é cacheado pelo próprio bundler, mas
// guardamos a promise aqui pra que chamadas concorrentes (preload + runLoad)
// compartilhem UM download, e pro preload poder ser disparado no mount da tela,
// EM PARALELO com o sign — nenhum dos dois depende do outro.
let mpegtsModPromise: Promise<MpegtsModule> | null = null

function importMpegts(): Promise<MpegtsModule> {
  if (!mpegtsModPromise) {
    mpegtsModPromise = import('mpegts.js').then(
      (m) => m.default as unknown as MpegtsModule,
    )
    // Falha não pode envenenar o cache: sem isso, uma queda de rede no preload
    // condenaria todo play() seguinte a rejeitar na mesma promise.
    mpegtsModPromise.catch(() => {
      mpegtsModPromise = null
    })
  }
  return mpegtsModPromise
}

/**
 * Aquece o chunk do mpegts.js SEM tocar em nenhum player.
 *
 * Chame no mount de uma tela que provavelmente vai tocar live (a grade da TV ao
 * Vivo): o download corre em paralelo com o sign, então quando o `play()`
 * acontece o módulo já está em memória. Fire-and-forget — erro aqui é
 * irrelevante (o `load()` real re-tenta e reporta).
 */
export function preloadMpegts(): void {
  void importMpegts().catch(() => {
    /* preload é best-effort: o load() real reporta o erro de verdade */
  })
}

/**
 * Opções de runtime do mpegts.js pro caminho de LIVE, por tier.
 *
 * ⚠️ HOJE: DEFAULTS DA LIB, de propósito. Não é esquecimento — é o resultado de
 * medir na LG real (perf/tv-live-cascade) e NÃO conseguir provar ganho.
 *
 * A hipótese era boa: 97% dos blocos de 585ms-1,7s do diagnóstico eram major GC
 * saindo de `_onSourceBufferUpdateEnd`, e `autoCleanupSourceBuffer` é `false` por
 * default na lib (ver `mpegts.js/src/config.js`) — numa live, que nunca termina,
 * o SourceBuffer acumula tudo desde o zap.
 *
 * O que a TV do dono disse (Chr53, tier `mid`, 90s de reprodução, A/B do MESMO
 * bundle mudando só estas opções):
 *   - afinado (autoCleanup + backward 60s/30s + stash OFF em mid+):
 *     28 blocos >500ms, pior 1479ms
 *   - defaults da lib: 15 blocos >500ms, pior 3341ms
 *   - e no boot da live o pior bloco piorou de forma consistente com o afinamento
 *     (1808ms/1771ms, contra 1233ms do bundle de referência)
 * Sinal misto e, no número de travadas, NEGATIVO. Sem ganho provado, o default
 * fica: a regra do time é não trocar um problema por outro.
 *
 * Erro de desenho que a medição expôs, pra quem retomar: eu havia DESLIGADO o
 * stash em mid+ — o tier desta TV. O stash é o buffer de IO antes do demuxer;
 * desligá-lo faz appends MENORES e MAIS FREQUENTES, ou seja, MAIS passagens por
 * `_onSourceBufferUpdateEnd` — o oposto de "append maior/menos frequente", que
 * era a direção pedida. Quem voltar ao tema deve testar o contrário: stash LIGADO
 * e MAIOR que os 384KB default, medindo o custo em latência do 1º frame (o stash
 * precisa encher antes do primeiro append, e isso empurra a meta do hero).
 *
 * Mantido como função (e não removido) porque o ponto de injeção no `runLoad` é
 * o lugar certo pra afinar, e o próximo experimento deve nascer aqui — com A/B
 * no MESMO cenário (player fullscreen; o hero preview da live não é comparável).
 */
export function mpegtsOptionsFor(_tier: Tier): MpegtsOptions {
  return {}
}

// Shaka tipos mínimos sem importar o módulo (lazy load).
interface ShakaPlayerInstance {
  attach(video: HTMLMediaElement): Promise<void>
  load(url: string, startTime?: number | null): Promise<void>
  /** Solta a MediaSource e PARA de baixar segments; mantém a instância
   *  viva e reutilizável por um `load()` posterior. */
  unload(): Promise<void>
  destroy(): Promise<void>
  configure(config: object): boolean
  addEventListener(event: string, listener: (e: Event) => void): void
}

interface ShakaModule {
  Player: new () => ShakaPlayerInstance
  polyfill: { installAll(): void }
  log?: { setLevel(level: number): void; Level: { ERROR: number; WARNING: number } }
}

/** Opções de construção do `WebPlayer`. */
export interface WebPlayerOptions {
  /**
   * Liga o ramo NATIVE-TS pro LIVE: aponta `video.src` direto pra URL `.ts` e
   * deixa o `<video>` nativo tocar, PULANDO o mpegts.js/MSE. Só deve vir `true`
   * da CLASSE provada em device (webOS gen 4-5, ver `canPlayNativeTs`) — o
   * `createPlayer` já resolve isso pela capability.
   *
   * `false` (default) = comportamento LEGADO byte-idêntico: todo LIVE passa pelo
   * mpegts.js. É a garantia de não-regressão pra web/mobile/TV-moderna/qualquer
   * device não-alvo — com o flag OFF, o ramo native-ts é código morto.
   */
  nativeTs?: boolean
}

/**
 * Player web unificado.
 *
 * ## Backends
 *
 * - **Shaka Player (MSE)** pra HLS, MP4 progressivo (parcial), DASH.
 *   • Lazy load (~250KB gz) — só carrega quando precisa.
 *   • ABR adaptativo, smart buffering, multi-track.
 *
 * - **mpegts.js** pra MPEG-TS (live + catchup Xtream timeshift).
 *   • Shaka não suporta MPEG-TS native. mpegts.js também lazy.
 *
 * - **`<video>` nativo** como fallback:
 *   • Safari + HLS direto (canPlayType vnd.apple.mpegurl).
 *   • TVs antigas sem MSE — vai pra `src` direto.
 *
 * ## Lifecycle (refator 2026-05-16)
 *
 * Garantias do contrato (ver `types.ts:MediaPlayer`):
 *
 * 1. **Fila interna** serializa `load()` e `destroy()` — nenhuma
 *    operação concorrente. Sem race entre Shaka soltar MediaSource e
 *    `<video>.load()` invalidar o MSE.
 *
 * 2. **`destroy()` é awaitable** — resolve APÓS Shaka/mpegts soltarem
 *    MSE E `<video>` ter sido resetado (`pause + removeAttribute('src')
 *    + load()`). Chamadores que recriam player no MESMO `<video>` DEVEM
 *    `await` — senão o próximo `attach()` acontece em cima de cleanup
 *    em andamento.
 *
 * 3. **Eventos pós-destroy bloqueados** — flag `acceptEvents` impede
 *    callbacks tardios (mpegts/Shaka emitindo erro no próprio shutdown).
 *
 * 4. **`<video>` é responsabilidade EXCLUSIVA do player** — consumers
 *    JAMAIS devem tocar em `video.src` / `removeAttribute('src')`. Usar
 *    sempre `attach()` / `destroy()`.
 *
 * 5. **Idempotência** — `destroy()` 2x é seguro; segundo no-op.
 *
 * 6. **`suspend()` / `resume()` (perf/tv-zapping, jul/26)** — `pause()` no
 *    `<video>` NÃO para o mpegts.js: sem `enableWorker` ele segue baixando
 *    e demuxando NA MAIN THREAD. `suspend()` aborta o loader e destrói o
 *    transmuxer sem soltar o `<video>`/MSE; `resume()` re-arma. Usado tanto
 *    na saída da rota de live quanto no início de cada zap (o canal velho
 *    não pode disputar CPU com o sign/load do novo).
 *
 * ## Histórico
 *
 * Antes do refator de 2026-05-16, `destroy()` era sync e disparava
 * `cleanupShaka/Mpegts` async sem await, em paralelo com `video.src`
 * cleanup. Race causava `<video>` mexido enquanto Shaka ainda detachava
 * MediaSource → exceptions silenciosas + handles pendurados. Bug
 * documentado em `docs/changelog/refactor-player-lifecycle.md`.
 */
export class WebPlayer implements MediaPlayer {
  // Polyfill da Shaka é instalado 1× por processo (custo Tizen 2017).
  private static shakaPolyfillInstalled = false

  private video: HTMLVideoElement | null = null
  private shaka: ShakaPlayerInstance | null = null
  private mpegts: MpegtsPlayer | null = null
  private tier: Tier
  private listeners = {
    ready: [] as EventHandler[],
    playing: [] as EventHandler[],
    paused: [] as EventHandler[],
    buffering: [] as EventHandler[],
    ended: [] as EventHandler[],
    error: [] as ErrorHandler[],
    mediainfo: [] as MediaInfoHandler[],
    destroyed: [] as EventHandler[],
  }

  /**
   * Fila serializa operações que mexem em recursos compartilhados
   * (Shaka instance, mpegts instance, `<video>` element). Garante que
   * `load()` e `destroy()` não correm em paralelo.
   *
   * IMPORTANTE: a fila NÃO impede `load()` mais recente de "vencer" o
   * anterior. Pra isso usamos `currentLoadToken` (cancel-and-replace) —
   * carga obsoleta detecta via token e aborta no próximo await.
   */
  private opQueue: Promise<void> = Promise.resolve()

  /**
   * Token do `load()` mais recente. Cada chamada de `load()` cria um
   * Symbol novo e atualiza esta ref. Cargas em andamento checam o token
   * em pontos-chave (após `await import()`, `await player.attach()`,
   * `await player.load()`) — se já foi substituído por outra `load()`,
   * abortam cedo + cleanup do recurso parcial.
   *
   * Por que: zapping de canais em fullscreen (Fernando 2026-05-16):
   * user aperta seta 5x rápido. Sem cancel-and-replace, a fila do
   * `opQueue` faria os 5 loads em sequência (~10s+ em rede ruim).
   * Com cancel-and-replace, só o último load completa; os 4 anteriores
   * abortam silenciosamente após o próximo await.
   */
  private currentLoadToken: symbol | null = null

  /**
   * Quando true, eventos do `<video>`/Shaka/mpegts são propagados pros
   * listeners externos. Quando false (durante e após destroy), callbacks
   * tardios viram no-op silencioso. Previne consumer recebendo evento de
   * player que já foi destruído.
   */
  private acceptEvents = true

  /**
   * True após `destroy()` ter completado. Não pode reusar instância —
   * tentativas de `load()`/`play()` lançam erro explícito (vs bug
   * silencioso em refs nullificadas).
   */
  private destroyed = false

  /**
   * True entre `suspend()` e o próximo `load()`. Marcado de forma SÍNCRONA
   * (antes da fila) pra que o caller saiba na hora que o pipeline está
   * condenado, mesmo com o `runSuspend` ainda na fila.
   */
  private suspended = false

  /**
   * Ramo NATIVE-TS ligado (classe provada). Quando `false` (default), o LIVE
   * segue byte-idêntico pelo mpegts.js — é a prova ESTRUTURAL de não-regressão.
   */
  private nativeTs: boolean

  /**
   * True enquanto o pipeline VIVO é o `<video>` nativo tocando `.ts` (ramo
   * native-ts), não mpegts/Shaka. Governa o teardown: no native-ts o pipeline É
   * o `video.src`, então `suspend()`/cleanup precisam RESETAR o src pra ceder o
   * único plano de HW (mpegts preserva o src; native-ts NÃO pode).
   */
  private nativeTsActive = false

  /**
   * Handler de `error` do `<video>` do ramo native-ts (o MediaError nativo).
   * Guardado pra desanexar no cleanup/suspend/destroy — é one-shot por load.
   */
  private nativeTsErrorHandler: ((e: Event) => void) | null = null

  /**
   * Handler de `error` do `<video>` nos ramos NATIVOS de VOD/série (mp4, HLS
   * nativo, fallback `src`). Até 27/09/2026 esses ramos não escutavam o elemento:
   * um `MediaError` (ex.: code 4 do 403 da origem) nunca virava `on('error')` e a
   * tela ficava em "Carregando" pra sempre. One-shot por load, mesmo ciclo de vida
   * do `nativeTsErrorHandler`.
   */
  private mediaErrorHandler: ((e: Event) => void) | null = null

  /**
   * True quando um `suspend()` do ramo native-ts JÁ resetou `video.src`
   * (removeAttribute + load) neste ciclo. O próximo `runLoad` CONSOME a flag pra
   * PULAR o `resetVideoSrc()` redundante: no zap native-ts o suspend já cedeu o
   * plano de HW, então um 2º `video.load()` só custa outro `emptied` e invalida o
   * gesture de autoplay à toa (medido na LG: 2 `video.load()`/zap).
   *
   * Escopo native-ts ESTRITO: só o ramo nativo seta a flag (mpegts/Shaka/fallback
   * NUNCA), então o `resetVideoSrc()` deles roda sempre — BYTE-IDÊNTICO ao legado.
   */
  private nativeTsSuspendReset = false

  /**
   * Recheck pendente do último `seek` feito com a duração ainda desconhecida (ver
   * `seek`). Guardado pra desarmar quando um `load()` novo troca a mídia ou o
   * player é destruído — um recheck órfão não pode limitar a mídia SEGUINTE.
   */
  private seekRecheck: { video: HTMLVideoElement; handler: () => void } | null = null

  constructor(tier: Tier = 'mid', opts: WebPlayerOptions = {}) {
    this.tier = tier
    this.nativeTs = opts.nativeTs ?? false
  }

  attach(element: HTMLVideoElement): void {
    if (this.destroyed) {
      throw new Error('WebPlayer: cannot attach after destroy()')
    }
    this.video = element
    element.addEventListener('playing', this.onPlaying)
    element.addEventListener('pause', this.onPaused)
    element.addEventListener('waiting', this.onBuffering)
    element.addEventListener('ended', this.onEnded)
  }

  // ===== Lifecycle público (serializado via opQueue) =====

  async load(source: MediaSource): Promise<void> {
    if (this.destroyed) {
      throw new Error('WebPlayer: cannot load() after destroy()')
    }
    // Cancel-and-replace: substitui token IMEDIATAMENTE. Loads anteriores
    // ainda na fila (ou em algum await interno) vão detectar token
    // diferente no próximo checkpoint e abortar cedo. O último load
    // sempre vence — comportamento esperado de zapping.
    const token = Symbol('load')
    this.currentLoadToken = token
    return this.enqueue(() => this.runLoad(source, token))
  }

  async destroy(): Promise<void> {
    // Idempotente: já destruído resolve direto.
    if (this.destroyed) return
    // Marca como destruído IMEDIATAMENTE pra que load()/attach()
    // subsequentes (durante destroy assíncrono) sejam rejeitados antes
    // de entrarem na fila.
    this.destroyed = true
    this.acceptEvents = false
    return this.enqueue(() => this.runDestroy())
  }

  /**
   * Para rede + demux AGORA, preservando `<video>`/MSE. Ver o contrato em
   * `types.ts:MediaPlayer.suspend`.
   *
   * Idempotente. Marca `suspended` de forma SÍNCRONA: um `load()` que
   * chegue logo em seguida (zapping) entra na fila DEPOIS do `runSuspend`
   * e desmarca. O ganho é o intervalo entre os dois — na TV, o sign do
   * canal novo (~300ms) deixa de disputar CPU com o demux do velho.
   *
   * Pra voltar a tocar depois de um suspend: `load()` normal (o `runLoad`
   * descarta o pipeline suspenso). NÃO há `resume()` — ver a nota no contrato.
   */
  async suspend(): Promise<void> {
    if (this.destroyed || this.suspended) return
    this.suspended = true
    return this.enqueue(() => this.runSuspend())
  }

  isSuspended(): boolean {
    return this.suspended && !this.destroyed
  }

  // ===== Comandos sync (não passam pela fila — atuam direto no video) =====

  async play(): Promise<void> {
    if (!this.video) return
    await this.video.play()
  }

  pause(): void {
    this.video?.pause()
  }

  /**
   * Seek com alvo SEGURO (ver `seek-target.ts`): não-finito é ignorado, negativo
   * vira 0, e com a duração conhecida o alvo nunca passa de `duração − margem`.
   *
   * No ramo nativo o `ready` sai logo depois do `video.src`, ANTES do metadata: a
   * duração é NaN e não dá pra limitar agora. O motor guarda o alvo e, no metadata,
   * prende no FIM se passar dele (→ `ended`). Então reconfere UMA vez no
   * `loadedmetadata`. Alvo válido passa intacto nos dois casos — os callers de
   * retomar (série, VOD, catch-up, mobile) não mudam de comportamento.
   */
  seek(position: number): void {
    const video = this.video
    if (!video) return
    const target = clampSeekTarget(position, video.duration)
    if (target === null) return
    this.disarmSeekRecheck()
    video.currentTime = target
    if (!isKnownDuration(video.duration)) this.armSeekRecheck(video, target)
  }

  private armSeekRecheck(video: HTMLVideoElement, target: number): void {
    const handler = (): void => {
      this.disarmSeekRecheck()
      if (this.destroyed || this.video !== video) return
      const fixed = clampSeekTarget(target, video.duration)
      if (fixed !== null && fixed < target) video.currentTime = fixed
    }
    this.seekRecheck = { video, handler }
    video.addEventListener('loadedmetadata', handler)
  }

  private disarmSeekRecheck(): void {
    const r = this.seekRecheck
    if (!r) return
    this.seekRecheck = null
    r.video.removeEventListener('loadedmetadata', r.handler)
  }

  // ===== Listeners =====

  on(event: 'ready' | 'playing' | 'paused' | 'buffering' | 'ended' | 'destroyed', handler: EventHandler): void
  on(event: 'error', handler: ErrorHandler): void
  on(event: 'mediainfo', handler: MediaInfoHandler): void
  on(event: keyof typeof this.listeners, handler: EventHandler | ErrorHandler | MediaInfoHandler): void {
    ;(this.listeners[event] as Array<EventHandler | ErrorHandler | MediaInfoHandler>).push(handler)
  }

  off(event: 'ready' | 'playing' | 'paused' | 'buffering' | 'ended' | 'destroyed', handler: EventHandler): void
  off(event: 'error', handler: ErrorHandler): void
  off(event: 'mediainfo', handler: MediaInfoHandler): void
  off(event: keyof typeof this.listeners, handler: EventHandler | ErrorHandler | MediaInfoHandler): void {
    const arr = this.listeners[event] as Array<EventHandler | ErrorHandler | MediaInfoHandler>
    const i = arr.indexOf(handler)
    if (i >= 0) arr.splice(i, 1)
  }

  // ===== Internals =====

  /**
   * Serializa operações pesadas (load/destroy) numa fila linear.
   *
   * Por que: load e destroy mexem em `this.shaka`/`this.mpegts`/`<video>`
   * com awaits no meio. Se duas correrem em paralelo, podem criar
   * recursos órfãos ou mexer no `<video>` enquanto a lib anterior ainda
   * detacha. A fila garante que `runDestroy()` completa ANTES de
   * `runLoad()` começar (e vice-versa).
   *
   * Falhas de uma op NÃO bloqueiam a próxima — `.catch(() => {})` mantém
   * a cadeia avançando. Mas o caller original ainda recebe a rejeição.
   */
  private enqueue(op: () => Promise<void>): Promise<void> {
    const next = this.opQueue.then(op, op)
    // Cadeia interna ignora falhas pra próximas ops avançarem.
    this.opQueue = next.catch(() => {})
    return next
  }

  private async runLoad(source: MediaSource, token: symbol): Promise<void> {
    // Token desatualizado: outra load() já substituiu. Aborta cedo —
    // sequer toca em recursos. Comportamento de zapping rápido.
    if (this.currentLoadToken !== token) return
    if (!this.video) throw new Error('Video element not attached')
    if (this.destroyed) throw new Error('WebPlayer: load() after destroy()')

    // Mídia nova: o recheck de um seek da mídia ANTERIOR não pode limitar esta.
    this.disarmSeekRecheck()

    // Um load() novo cancela qualquer suspend pendente: o pipeline que
    // montamos aqui É o vivo (e descarta o suspenso no cleanup abaixo).
    this.suspended = false

    // Cleanup do backend anterior. Se nada estiver ativo, no-op.
    await this.cleanupShaka()
    await this.cleanupMpegts()
    // Desanexa o handler de erro do ramo native-ts anterior (se houve) —
    // antes de o resetVideoSrc mexer no <video>.
    this.detachNativeTsError()
    // Idem o dos ramos nativos de VOD/série: o reset do src não pode virar erro
    // atribuído à mídia NOVA.
    this.detachMediaError()
    this.nativeTsActive = false
    // Reset defensivo do <video> nativo (caso o fallback src OU o ramo
    // native-ts anterior tenha usado — cleanupShaka/Mpegts não cobrem isso).
    // Também CEDE o plano de HW do native-ts anterior (pause + remove src + load).
    //
    // A2 (perf/tv-live-native-ts-reuse): num zap native-ts o `runSuspend` já fez
    // esse reset (removeAttribute + load) antes deste load entrar na fila. Fazê-lo
    // de novo é um 2º `video.load()` redundante (outro `emptied` + gesture
    // invalidado, sem ceder nada — o src já está limpo). Consome a flag e pula.
    // A flag só é setada pelo ramo native-ts: mpegts/Shaka/fallback caem sempre no
    // `resetVideoSrc()` — comportamento byte-idêntico ao legado preservado.
    if (this.nativeTsSuspendReset) {
      this.nativeTsSuspendReset = false
    } else {
      this.resetVideoSrc()
    }

    // Checkpoint pós-cleanup: pode demorar (Shaka destroy é async).
    if (this.currentLoadToken !== token) return

    // ===== MPEG-TS (live + catchup Xtream) =====
    if (source.type === 'mpegts' || source.type === 'catchup') {
      // ===== Ramo NATIVE-TS (webOS provado) — só LIVE, capability-gated =====
      // ADITIVO: `this.nativeTs` só é true na CLASSE provada (canPlayNativeTs, via
      // createPlayer). Em QUALQUER outra stack é false e este `if` é código morto
      // — o LIVE segue reto pro mpegts.js abaixo, BYTE-IDÊNTICO ao caminho legado.
      // Catchup (`source.type === 'catchup'`) NUNCA entra aqui: só o LIVE
      // ('mpegts') foi provado no `<video>` nativo; o timeshift fica no mpegts.js.
      if (this.nativeTs && source.type === 'mpegts') {
        this.loadNativeTs(source, token)
        return
      }
      // Se a tela chamou `preloadMpegts()` no mount, isto resolve na hora — o
      // chunk baixou EM PARALELO com o sign em vez de esperar a resposta dele.
      const mpegtsMod = await importMpegts()
      // Checkpoint pós-import (lazy chunk pode demorar em vlow).
      if (this.currentLoadToken !== token) return
      if (!mpegtsMod.isSupported()) {
        throw new Error('MPEG-TS not supported on this platform')
      }
      // isLive=true mesmo pra catch-up: timeshift Xtream serve como stream
      // contínuo (sem Accept-Ranges/Content-Length). Trade-off: sem seek.
      const player = mpegtsMod.createPlayer(
        {
          type: 'mpegts',
          url: source.url,
          isLive: true,
        },
        mpegtsOptionsFor(this.tier),
      )
      // CRÍTICO: registra handler de error ANTES de attach/load.
      // mpegts.js usa EventEmitter customizado — sem handler, qualquer
      // erro vira "Uncaught (in promise)" no console + crashada interna.
      // Evento 'error' chega com 3 args POSICIONAIS:
      // (errorType, errorDetail, errorInfo). Ver nota em `MpegtsPlayer.on`.
      player.on('error', (...args: unknown[]) => {
        const errorType = typeof args[0] === 'string' ? args[0] : 'unknown'
        const detail = typeof args[1] === 'string' ? args[1] : ''
        const info = (args[2] ?? {}) as MpegtsErrorInfo
        // Em NetworkError/HttpStatusCodeInvalid o `code` é o status HTTP da
        // origem. É ele que substitui o `fetch HEAD` de pre-check: o próprio
        // GET do stream já revela 403/451 na hora, sem custar um round-trip
        // extra ANTES de todo zap.
        const httpStatus =
          typeof info.code === 'number' && info.code >= 100 && info.code <= 599
            ? info.code
            : undefined
        this.emitError({
          fatal: true,
          code: `mpegts:${errorType}`,
          message: `${detail}${info.code ? ` (${info.code})` : ''} ${info.msg ?? ''}`.trim(),
          // exactOptionalPropertyTypes: nada de `httpStatus: undefined`.
          ...(httpStatus !== undefined ? { httpStatus } : {}),
        })
      })
      // FONTE DE VERDADE de "tem vídeo": o demuxer reporta `hasVideo`
      // (parseado do PMT) via MEDIA_INFO — ANTES do 1º frame decodar. É o que
      // resolve o falso-positivo de "audio-only": o consumer não precisa mais
      // adivinhar por `video.videoWidth===0` num instante racy (áudio decoda
      // antes do 1º keyframe). Rádio real vem `hasVideo:false`; canal de vídeo
      // vem `hasVideo:true` mesmo com o frame ainda não pintado.
      player.on(mpegtsMod.Events.MEDIA_INFO, (...args: unknown[]) => {
        // Token desatualizado (zap) ou player já em teardown → descarta: um
        // MEDIA_INFO tardio do canal VELHO (ex: rádio) não pode marcar
        // audio-only no canal NOVO. (`emitMediaInfo` também respeita
        // `acceptEvents`, mas o token guarda o intervalo pré-cleanup.)
        if (this.currentLoadToken !== token) return
        const mi = (args[0] ?? {}) as MpegtsMediaInfo
        this.emitMediaInfo({
          // Normaliza pra boolean estrito: só `true` conta como "tem vídeo".
          hasVideo: mi.hasVideo === true,
          // exactOptionalPropertyTypes: sem chaves `undefined`.
          ...(typeof mi.videoCodec === 'string' ? { videoCodec: mi.videoCodec } : {}),
          ...(typeof mi.width === 'number' ? { width: mi.width } : {}),
          ...(typeof mi.height === 'number' ? { height: mi.height } : {}),
        })
      })
      // Race guard: destroy() entrou na fila OU outro load() substituiu
      // o token. Em ambos, descartar este player parcial.
      if (this.destroyed || !this.video || this.currentLoadToken !== token) {
        try { player.destroy() } catch { /* ignore */ }
        return
      }
      this.mpegts = player
      player.attachMediaElement(this.video)
      player.load()
      if (source.startPosition && this.video) this.video.currentTime = source.startPosition
      this.emit('ready')
      return
    }

    // ===== MP4 progressivo: <video> nativo direto =====
    // Shaka NÃO suporta MP4 progressivo monolithic (só fMP4 fragmentado).
    // MP4 do Xtream é tipicamente monolithic com moov-no-fim — sofre TTFF
    // alto independente de player. Solução real seria reescrever pra fMP4
    // no proxy (custo CPU alto). Por ora <video> nativo é o caminho.
    if (source.type === 'mp4') {
      this.attachMediaError(source.url, token)
      this.video.src = source.url
      if (source.startPosition) this.video.currentTime = source.startPosition
      this.emit('ready')
      return
    }

    // ===== Safari nativo HLS (sempre prefere quando suportado) =====
    if (source.type === 'hls' && this.video.canPlayType('application/vnd.apple.mpegurl')) {
      this.attachMediaError(source.url, token)
      this.video.src = source.url
      if (source.startPosition) this.video.currentTime = source.startPosition
      this.emit('ready')
      return
    }

    // ===== Shaka Player (HLS streaming + DASH) =====
    if (this.isMseSupported()) {
      try {
        await this.loadWithShaka(source, token)
        return
      } catch (err) {
        // Outro load() substituiu o token enquanto Shaka inicializava —
        // descartar silenciosamente, não cair pro fallback nativo (que
        // sobrescreveria o load mais recente).
        if (this.currentLoadToken !== token) return
        // Fallback pra <video> nativo se shaka explodir (corner cases)
        console.warn('[WebPlayer] shaka falhou, caindo pra <video> nativo:', err)
      }
    }

    // ===== Fallback nativo <video src> =====
    // Checkpoint final: descarta se token mudou durante fallback path.
    if (this.currentLoadToken !== token || !this.video) return
    // TVs sem MSE (Tizen 2.x/3.x raríssimas) OU shaka explodiu.
    this.attachMediaError(source.url, token)
    this.video.src = source.url
    if (source.startPosition) this.video.currentTime = source.startPosition
    this.emit('ready')
  }

  private async loadWithShaka(source: MediaSource, token: symbol): Promise<void> {
    if (!this.video) throw new Error('Video not attached')

    const shaka = (await import('shaka-player/dist/shaka-player.compiled')) as unknown as ShakaModule
    // Checkpoint pós-import (chunk Shaka é ~250KB gz).
    if (this.currentLoadToken !== token) return

    // Polyfills cobrem MSE/EME quirks em browsers/TVs. Cache estático:
    // shaka.polyfill.installAll() é idempotente mas custa CPU em Tizen
    // 2017 (re-registra registry global). Instalamos 1× por processo.
    if (!WebPlayer.shakaPolyfillInstalled) {
      shaka.polyfill.installAll()
      WebPlayer.shakaPolyfillInstalled = true
    }

    // Reduz log nivel em prod. Detecta via location.hostname (sem
    // depender de import.meta.env do Vite — package é portável).
    const isProd =
      typeof location !== 'undefined' &&
      !/(^localhost$|^127\.|^192\.168\.|^10\.|\.local$)/.test(location.hostname)
    if (shaka.log && isProd) {
      shaka.log.setLevel(shaka.log.Level.ERROR)
    }

    const player = new shaka.Player()
    await player.attach(this.video)

    // Race guard: destroy() entrou na fila OU outro load() substituiu o
    // token enquanto importava shaka / attachava. Destrói player parcial
    // e aborta — não atribui a `this.shaka`.
    if (this.destroyed || !this.video || this.currentLoadToken !== token) {
      try { await player.destroy() } catch { /* ignore */ }
      return
    }

    // Config baseado em tier do device:
    // - vlow/low: buffer menor (RAM limitada), retry mais agressivo
    // - mid+: buffer maior, ABR aggressive
    const isLowTier = this.tier === 'vlow' || this.tier === 'low'
    player.configure({
      streaming: {
        bufferingGoal: isLowTier ? 10 : 30, // segundos pré-buffer
        rebufferingGoal: isLowTier ? 2 : 4,
        bufferBehind: isLowTier ? 15 : 30,
        retryParameters: {
          maxAttempts: 3,
          baseDelay: 1000,
          backoffFactor: 2,
          fuzzFactor: 0.5,
          timeout: 20000,
        },
      },
      abr: {
        enabled: true,
        defaultBandwidthEstimate: isLowTier ? 1_500_000 : 5_000_000, // bps
      },
    })

    player.addEventListener('error', (e: Event) => {
      const detail = (
        e as Event & {
          detail?: { code?: number; message?: string; data?: unknown[] }
        }
      ).detail
      // Shaka BAD_HTTP_STATUS (1001): `data = [uri, status, body, headers,
      // requestType]`. É a contraparte do httpStatus do mpegts — permite
      // classificar bloqueio (403/451) sem o HEAD bloqueante do zap.
      const rawStatus = detail?.code === SHAKA_BAD_HTTP_STATUS ? detail.data?.[1] : undefined
      const httpStatus =
        typeof rawStatus === 'number' && rawStatus >= 100 && rawStatus <= 599
          ? rawStatus
          : undefined
      this.emitError({
        fatal: true,
        code: String(detail?.code ?? 'unknown'),
        message: detail?.message ?? 'Shaka playback error',
        ...(httpStatus !== undefined ? { httpStatus } : {}),
      })
    })

    this.shaka = player
    await player.load(source.url, source.startPosition ?? null)
    // Checkpoint pós-load: stream pode ter levado segundos. Se outro
    // load() substituiu o token, descarta — `this.shaka` será limpo
    // pelo próximo runLoad via cleanupShaka.
    if (this.currentLoadToken !== token) return
    this.emit('ready')
  }

  /**
   * Ramo NATIVE-TS: aponta o `<video>` direto pra URL `.ts` e deixa o decode de
   * HARDWARE do webOS tocar, SEM mpegts.js/MSE. Só é chamado quando `this.nativeTs`
   * (classe provada) E `source.type === 'mpegts'` (live).
   *
   * 🔒 CREDENCIAL NO DOM (cybersec): a URL `.ts` do Xtream embute user/pass no
   * path. Com `video.src = url` a credencial fica LEGÍVEL no atributo `src` do
   * `<video>` — coisa que o MSE/mpegts NÃO faz (passa por buffer). É a MESMA
   * credencial que o mpegts.js já manda na rede, mas agora inspecionável no DOM.
   * Aceito porque é a única forma de o decode NATIVO tocar. MITIGAÇÃO: NADA neste
   * ramo pode logar/telemetriar a URL crua — todo sink de texto (log/erro/QoE)
   * passa por `redactStreamUrl()`. O `video.src` em si é inevitável (o navegador
   * exige a URL real pra tocar), mas não sangra pra nenhum outro lugar.
   */
  private loadNativeTs(source: MediaSource, token: symbol): void {
    if (!this.video) return
    // Race guard: destroy()/outro load() já em curso — descarta.
    if (this.destroyed || this.currentLoadToken !== token) return

    // O `<video>` nativo só dá `MediaError` (sem status HTTP). Anexa o handler
    // ANTES de setar o src, one-shot por load (desanexa a si mesmo no 1º erro).
    const onNativeError = (): void => {
      this.detachNativeTsError()
      if (!this.acceptEvents || this.currentLoadToken !== token) return
      // fetch-probe lateral (Range 0-0), SÓ no erro, recupera o status HTTP
      // (403/451) que o MediaError não traz — pra alimentar a MESMA máquina de
      // failover/geo-block que o mpegts usa via `PlayerErrorPayload.httpStatus`.
      const media = this.video?.error
      const mediaCode = media && typeof media.code === 'number' ? media.code : 'unknown'
      void this.probeNativeTsStatus(source.url, token, `native-ts:${mediaCode}`)
    }
    this.nativeTsErrorHandler = onNativeError
    this.video.addEventListener('error', onNativeError)

    this.nativeTsActive = true
    // 🔒 credencial vai pro DOM aqui (ver doc acima) — e SÓ aqui.
    this.video.src = source.url
    if (source.startPosition) this.video.currentTime = source.startPosition
    this.emit('ready')
  }

  /**
   * Recupera o status HTTP da origem DEPOIS de um erro do `<video>` nativo, via
   * fetch-probe lateral (Range 0-0), e emite o `error` com esse `httpStatus` —
   * alimentando o failover/geo-block igual ao mpegts. Emite EXATAMENTE uma vez
   * (bounded por timeout: probe travado não segura o erro pra sempre).
   *
   * 🔒 A mensagem do erro usa `redactStreamUrl()` — a URL crua NUNCA vaza aqui.
   */
  private async probeNativeTsStatus(
    url: string,
    token: symbol,
    code: string,
    // Os ramos nativos de VOD/série reusam este probe (ver `attachMediaError`).
    // Defaults = o comportamento do native-ts, byte-idêntico.
    extra: Pick<PlayerErrorPayload, 'mediaErrorCode' | 'beforeMetadata'> = {},
    label = 'native-ts playback error',
    // Ramos nativos de VOD/série: mede também se a ORIGEM respondeu (HEAD no-cors),
    // em PARALELO e sob o mesmo prazo. O native-ts (live) não mede: byte-idêntico.
    checkReachability = false,
  ): Promise<void> {
    const [status, reachable] = await Promise.all([
      this.withProbeDeadline(this.fetchProbeStatus(url)),
      checkReachability ? this.withProbeDeadline(this.fetchOriginReachable(url)) : Promise.resolve(undefined),
    ])
    const httpStatus = typeof status === 'number' ? status : undefined
    // Load trocou (zap) ou player destruído durante o probe → descarta.
    if (!this.acceptEvents || this.currentLoadToken !== token) return
    this.emitError({
      fatal: true,
      code,
      // 🔒 host redigido — sem credencial. exactOptionalPropertyTypes: nada de
      // `httpStatus: undefined`.
      message: `${label} (${redactStreamUrl(url)})`,
      ...(httpStatus !== undefined ? { httpStatus } : {}),
      ...(reachable !== undefined ? { originReachable: reachable } : {}),
      ...extra,
    })
  }

  /** Resolve com o valor de `p` ou `undefined` no prazo do probe (race: assenta mesmo
   *  onde `signal` é inerte). Nunca rejeita. Limpa o próprio timer. */
  private async withProbeDeadline<T>(p: Promise<T | undefined>): Promise<T | undefined> {
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      return await Promise.race([
        p,
        new Promise<undefined>((resolve) => {
          timer = setTimeout(() => resolve(undefined), NATIVE_TS_PROBE_TIMEOUT_MS)
        }),
      ])
    } catch {
      return undefined
    } finally {
      if (timer !== undefined) clearTimeout(timer)
    }
  }

  /**
   * A origem respondeu ALGUMA coisa? `HEAD` com `mode: 'no-cors'`: método simples,
   * sem cabeçalho → SEM preflight (o `Range` do `fetchProbeStatus` exige preflight
   * CORS no Chr53, e a origem Xtream pode não responder `OPTIONS` — aí aquele probe
   * rejeita igual a uma rede caída). Com `fetch` NATIVO (Chr42+) a resposta é opaca
   * (sem status), mas a promise RESOLVE com qualquer resposta, inclusive 403, e
   * REJEITA só se a rede não chegou.
   *
   * No Chr38-41 isso é FALSO: o `fetch` é o whatwg-fetch (XHR com CORS, o `mode` só
   * fica guardado), e uma origem sem CORS rejeita igual a uma rede caída. Ali a sonda
   * não separa os dois e devolve `'unknown'` sem disparar nada. Devolver `false`
   * (rede) fazia a cópia morta virar "Confira sua internet" depois das novas
   * tentativas, e o failover entre cópias nunca olhava pra ela (review, 28/09/2026).
   * 🔒 Não lê corpo nem cabeçalho; a URL não é logada.
   */
  private async fetchOriginReachable(url: string): Promise<boolean | 'unknown'> {
    if (isFetchPolyfilled()) return 'unknown'
    try {
      await fetch(url, { method: 'HEAD', mode: 'no-cors' })
      return true
    } catch {
      return false
    }
  }

  /**
   * Escuta o `error` do `<video>` num ramo NATIVO de VOD/série e o transforma em
   * `on('error')` — com o status HTTP da origem recuperado pelo MESMO probe lateral
   * do native-ts (Range 0-0, prazo por race: assenta até no Chr53, onde `signal`
   * é inerte). Anexar ANTES de apontar o `src`: o erro pode chegar logo.
   *
   * `MEDIA_ERR_ABORTED` (1) é ignorado: é o próprio agente abortando a carga (troca
   * de `src`), não falha de reprodução.
   */
  private attachMediaError(url: string, token: symbol): void {
    if (!this.video) return
    this.detachMediaError()
    const video = this.video
    const onError = (): void => {
      this.detachMediaError()
      if (!this.acceptEvents || this.currentLoadToken !== token || this.video !== video) return
      const media = video.error
      const mediaCode = media && typeof media.code === 'number' ? media.code : undefined
      if (mediaCode === 1) return
      const beforeMetadata = video.readyState === 0
      void this.probeNativeTsStatus(
        url,
        token,
        `media:${mediaCode ?? 'unknown'}`,
        { ...(mediaCode !== undefined ? { mediaErrorCode: mediaCode } : {}), beforeMetadata },
        'media playback error',
        true,
      )
    }
    this.mediaErrorHandler = onError
    video.addEventListener('error', onError)
  }

  /** Desanexa o handler de erro dos ramos nativos de VOD/série. Idempotente. */
  private detachMediaError(): void {
    if (this.mediaErrorHandler && this.video) {
      this.video.removeEventListener('error', this.mediaErrorHandler)
    }
    this.mediaErrorHandler = null
  }

  /**
   * Faz UM fetch com Range 0-0 e devolve só o status HTTP (100-599), ou
   * `undefined`. Best-effort: cancela o corpo pra não drenar o stream. NUNCA
   * lança (todo erro vira `undefined`).
   */
  private async fetchProbeStatus(url: string): Promise<number | undefined> {
    try {
      const resp = await fetch(url, { method: 'GET', headers: { Range: 'bytes=0-0' } })
      const s = resp.status
      // Não drena o corpo do stream: só queríamos o status. Guardado — em engine
      // sem ReadableStream (polyfill de fetch), `body` é null/undefined → no-op.
      try {
        void resp.body?.cancel()
      } catch {
        /* ignore */
      }
      return typeof s === 'number' && s >= 100 && s <= 599 ? s : undefined
    } catch {
      return undefined
    }
  }

  /** Desanexa (e esquece) o handler de erro do ramo native-ts. Idempotente. */
  private detachNativeTsError(): void {
    if (this.nativeTsErrorHandler && this.video) {
      this.video.removeEventListener('error', this.nativeTsErrorHandler)
    }
    this.nativeTsErrorHandler = null
  }

  /**
   * Ordem importa: pausa o `<video>` (para decode/render) ANTES de matar
   * o pipeline, senão o element fica pedindo dados de um transmuxer que
   * já morreu e pode disparar 'waiting'/'stalled' espúrio pros listeners.
   */
  private async runSuspend(): Promise<void> {
    if (this.destroyed) return

    if (this.video) {
      try {
        this.video.pause()
      } catch {
        /* ignore */
      }
    }
    // Player suspenso não reporta erro da mídia que ele mesmo está soltando.
    this.detachMediaError()

    // Ramo native-ts: NÃO há mpegts/Shaka pra dar unload — o pipeline É o
    // `video.src`, e `pause()` sozinho NÃO cede o plano de HW (o `<video>` segue
    // segurando o decode). Só resetar o src (pause + remove + load) cede o
    // ÚNICO plano ao próximo canal. É seguro porque o retorno é sempre um
    // `load()` completo (não há resume() — ver o contrato em types.ts).
    if (this.nativeTsActive) {
      this.detachNativeTsError()
      this.nativeTsActive = false
      if (this.video) {
        this.video.removeAttribute('src')
        this.video.load()
        // A2: sinaliza pro próximo `runLoad` que o src JÁ foi resetado aqui — ele
        // pula o `resetVideoSrc()` redundante (evita o 2º `video.load()` do zap).
        this.nativeTsSuspendReset = true
      }
      return
    }

    if (this.mpegts) {
      try {
        // O ponto do fix: aborta o fetch do stream e destrói o
        // transmuxer. Sem isso o demux segue rodando na main thread.
        this.mpegts.unload()
      } catch (err) {
        console.warn('[WebPlayer] mpegts.unload error (non-fatal):', err)
      }
    }

    if (this.shaka) {
      try {
        await this.shaka.unload()
      } catch (err) {
        console.warn('[WebPlayer] shaka.unload error (non-fatal):', err)
      }
    }
  }

  private async runDestroy(): Promise<void> {
    // Desregistra listeners do <video> ANTES de aguardar cleanups —
    // garante que eventos disparados durante o shutdown não chamam
    // callbacks externos (acceptEvents=false já bloqueia, mas é dupla
    // proteção contra evento dispatch em element órfão).
    this.detachVideoListeners()
    // Recheck de seek pendente também sai (long-session: cleanup ativo).
    this.disarmSeekRecheck()
    // Handler de erro do ramo native-ts também sai aqui.
    this.detachNativeTsError()
    this.detachMediaError()
    this.nativeTsActive = false
    // A2: o teardown reseta o src por conta própria abaixo — zera a flag pra não
    // deixar um estado "src já limpo" pendurado (defensivo; instância destruída
    // não recarrega, mas mantém o invariante da flag honesto).
    this.nativeTsSuspendReset = false

    // Aguarda Shaka soltar MediaSource COMPLETAMENTE antes de tocar
    // no <video>. Se rodar em paralelo, o video.load() abaixo invalida
    // o MSE que Shaka ainda está detachando.
    await this.cleanupShaka()
    await this.cleanupMpegts()

    // Só agora reseta o <video>. Pause primeiro pra parar fetch nativo
    // (caso fallback src tenha sido usado).
    if (this.video) {
      try { this.video.pause() } catch { /* ignore */ }
      this.video.removeAttribute('src')
      this.video.load()
      this.video = null
    }

    // Sinaliza pra consumers que destroy completou (ex: singleton
    // serializa ensurePlayer() esperando esse evento).
    this.emitDestroyed()

    // Limpa arrays — proteção redundante (acceptEvents=false já bloqueia).
    Object.values(this.listeners).forEach((arr) => (arr.length = 0))
  }

  private detachVideoListeners(): void {
    if (!this.video) return
    this.video.removeEventListener('playing', this.onPlaying)
    this.video.removeEventListener('pause', this.onPaused)
    this.video.removeEventListener('waiting', this.onBuffering)
    this.video.removeEventListener('ended', this.onEnded)
  }

  private resetVideoSrc(): void {
    if (!this.video) return
    try { this.video.pause() } catch { /* ignore */ }
    this.video.removeAttribute('src')
    this.video.load()
  }

  private async cleanupShaka(): Promise<void> {
    if (!this.shaka) return
    const player = this.shaka
    this.shaka = null
    try {
      await player.destroy()
    } catch (err) {
      // Erro de cleanup não é falha de playback — não emite 'error'
      // (poluiria UI). Log estruturado pra debug em prod.
      console.warn('[WebPlayer] cleanupShaka error (non-fatal):', err)
    }
  }

  private async cleanupMpegts(): Promise<void> {
    if (!this.mpegts) return
    const player = this.mpegts
    this.mpegts = null
    try {
      player.destroy()
    } catch (err) {
      console.warn('[WebPlayer] cleanupMpegts error (non-fatal):', err)
    }
  }

  private isMseSupported(): boolean {
    return typeof window !== 'undefined' && 'MediaSource' in window
  }

  // ===== Emit (com guard de acceptEvents) =====

  private emit(event: 'ready' | 'playing' | 'paused' | 'buffering' | 'ended'): void {
    if (!this.acceptEvents) return
    // Snapshot do array — handlers podem chamar off() durante iteração.
    const handlers = this.listeners[event].slice()
    for (const h of handlers) {
      try { h() } catch (err) {
        console.warn(`[WebPlayer] handler de '${event}' lançou:`, err)
      }
    }
  }

  private emitError(err: PlayerErrorPayload): void {
    if (!this.acceptEvents) return
    const handlers = this.listeners.error.slice()
    for (const h of handlers) {
      try { h(err) } catch (e) {
        console.warn('[WebPlayer] handler de error lançou:', e)
      }
    }
  }

  private emitMediaInfo(info: PlayerMediaInfo): void {
    if (!this.acceptEvents) return
    const handlers = this.listeners.mediainfo.slice()
    for (const h of handlers) {
      try { h(info) } catch (e) {
        console.warn('[WebPlayer] handler de mediainfo lançou:', e)
      }
    }
  }

  /**
   * Emite 'destroyed' EXATAMENTE uma vez. Não passa por acceptEvents
   * (esse evento É o sinal de fim — precisa chegar mesmo após shutdown).
   */
  private emitDestroyed(): void {
    const handlers = this.listeners.destroyed.slice()
    for (const h of handlers) {
      try { h() } catch (err) {
        console.warn('[WebPlayer] handler de destroyed lançou:', err)
      }
    }
  }

  private onPlaying = () => this.emit('playing')
  private onPaused = () => this.emit('paused')
  private onBuffering = () => this.emit('buffering')
  private onEnded = () => this.emit('ended')
}
